import { FC, useMemo, useState } from 'react';

import {
  SendCourtesyMessageResult,
  sendCourtesyMessage,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  giftCouponRecipientUrl,
  giftRecipientBaseUrl,
} from '@shared/utils/giftRecipientLink.util';

interface CourtesyMessageModalProps {
  /** 답례 발송 대상으로 선택된 쿠폰 (수신번호 보유분) */
  coupons: GiftCouponSummary[];
  onClose: () => void;
  /** 발송 성공 후(닫기 시) 호출 — 부모가 선택/모드를 정리한다 */
  onSent?: () => void;
}

const REASON_LABEL: Record<string, string> = {
  NO_PHONE: '수신번호 없음',
  NOT_FOUND: '쿠폰 없음',
};

export const CourtesyMessageModal: FC<CourtesyMessageModalProps> = ({
  coupons,
  onClose,
  onSent,
}) => {
  const [message, setMessage] = useState('');
  // 답례품 받기 링크의 프론트 도메인 — 기본값은 현재 접속 도메인.
  // 수신자 전용 도메인(REACT_APP_GIFT_RECIPIENT_BASE_URL)이 설정된 환경에서는 서버가
  // 자체 env(GIFT_RECIPIENT_BASE_URL)로 단축경로 링크를 조립하며 이 값은 무시된다.
  // (env 미설정 구서버 폴백용으로 계속 전송)
  const [baseUrl, setBaseUrl] = useState(window.location.origin);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SendCourtesyMessageResult | null>(null);

  // couponId → 코드/번호 역참조 (결과 표시용)
  const couponById = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of coupons) map.set(c._id, c);
    return map;
  }, [coupons]);

  const sendableCount = coupons.length;
  const trimmedBase = baseUrl.trim().replace(/\/+$/, '');
  const sampleCoupon = coupons[0];
  // 링크는 쿠폰 code 기반 (서버가 couponIds → code 매핑). 요청은 _id 그대로 전송.
  const previewLink = giftRecipientBaseUrl
    ? giftCouponRecipientUrl(sampleCoupon?.code ?? '{couponCode}')
    : sampleCoupon
    ? `${trimmedBase || '{baseUrl}'}/gift-store/coupon/${sampleCoupon.code}`
    : `${trimmedBase || '{baseUrl}'}/gift-store/coupon/{couponCode}`;

  const handleSend = async () => {
    if (sendableCount === 0) {
      setError('발송할 쿠폰이 없습니다.');
      return;
    }
    if (!message.trim()) {
      setError('답례글 본문을 입력해주세요.');
      return;
    }
    if (!baseUrl.trim()) {
      setError('답례품 받기 링크 도메인(baseUrl)을 입력해주세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await sendCourtesyMessage({
        couponIds: coupons.map((c) => c._id),
        message: message.trim(),
        baseUrl: baseUrl.trim(),
      });
      setResult(res);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '답례 인사 발송에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onSent?.();
    onClose();
  };

  const labelOf = (couponId: string) => {
    const c = couponById.get(couponId);
    return c ? `#${c.seq} · ${c.code}` : couponId;
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[88vh] w-full max-w-lg flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-rose-800">답례 인사 일괄 발송</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              수신번호 있는{' '}
              <span className="font-bold text-rose-600">{sendableCount}건</span>에 답례글
              LMS를 발송합니다.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {!result ? (
          <>
            {/* 본문 입력 */}
            <label className="block text-sm">
              <span className="font-medium text-gray-700">답례글 본문</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder={
                  '예) 감사의 인사를 드립니다.\n따뜻한 마음에 감사드립니다.\n홍길동 드림'
                }
                className="mt-1 w-full resize-none rounded-lg border px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
              <span className="mt-0.5 block text-xs text-gray-400">
                줄바꿈 포함 가능 · 본문 아래에 "답례품 받기" 링크가 자동으로 붙습니다.
              </span>
            </label>

            {/* baseUrl — 수신자 전용 도메인 환경에서는 서버가 도메인을 결정하므로 입력 불필요 */}
            {giftRecipientBaseUrl ? (
              <div className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                답례품 받기 링크는 수신자 전용 도메인(
                <span className="font-mono font-semibold">{giftRecipientBaseUrl}</span>
                )으로 발송됩니다.
              </div>
            ) : (
              <label className="block text-sm">
                <span className="font-medium text-gray-700">답례품 받기 링크 도메인</span>
                <input
                  type="text"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder="https://bugo.gaonprime.com"
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                />
              </label>
            )}

            {/* 실제 발송 문자 미리보기 */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="mb-1 text-xs font-semibold text-gray-500">
                실제 발송 문자 미리보기 (LMS · 제목 "답례 인사")
              </p>
              <div className="whitespace-pre-wrap break-words text-gray-700">
                {message.trim() || <span className="text-gray-400">{'{본문}'}</span>}
                {'\n\n답례품 받기\n'}
                <span className="text-rose-600">{previewLink}</span>
              </div>
              {sendableCount > 1 && (
                <p className="mt-1 text-[11px] text-gray-400">
                  링크의 쿠폰 코드는 수신자별로 각각 다르게 들어갑니다. (예시는{' '}
                  {sampleCoupon ? labelOf(sampleCoupon._id) : '{couponCode}'})
                </p>
              )}
            </div>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                닫기
              </button>
              <button
                onClick={handleSend}
                disabled={submitting || sendableCount === 0}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {submitting ? '발송중…' : `${sendableCount}건 발송`}
              </button>
            </div>
          </>
        ) : (
          /* 발송 결과 */
          <>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-emerald-50 px-2 py-3">
                <p className="text-xs text-emerald-600">발송 성공</p>
                <p className="text-xl font-bold text-emerald-700">{result.sentCount}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-3">
                <p className="text-xs text-gray-500">건너뜀</p>
                <p className="text-xl font-bold text-gray-600">{result.skippedCount}</p>
              </div>
              <div className="rounded-lg bg-red-50 px-2 py-3">
                <p className="text-xs text-red-500">실패</p>
                <p className="text-xl font-bold text-red-600">{result.failedCount}</p>
              </div>
            </div>
            <p className="text-center text-xs text-gray-400">
              총 {result.total}건 처리 (중복 제거 후)
            </p>

            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-3">
                <p className="mb-1 text-xs font-semibold text-gray-500">
                  건너뛴 쿠폰 ({result.skipped.length})
                </p>
                <ul className="space-y-0.5 text-xs text-gray-600">
                  {result.skipped.map((s) => (
                    <li key={s.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(s.couponId)}</span>
                      <span className="text-gray-400">
                        {REASON_LABEL[s.reason] ?? s.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.failed.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
                <p className="mb-1 text-xs font-semibold text-red-600">
                  발송 실패 ({result.failed.length})
                </p>
                <ul className="space-y-0.5 text-xs text-red-700">
                  {result.failed.map((f) => (
                    <li key={f.couponId} className="flex justify-between gap-2">
                      <span>{labelOf(f.couponId)}</span>
                      <span className="truncate text-red-400" title={f.reason}>
                        {f.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleDone}
                className="rounded-lg bg-rose-600 px-5 py-2 text-sm font-bold text-white hover:bg-rose-700"
              >
                확인
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
